/**
 * Scroll Performance Utilities
 * Optimized scroll event handling for better mobile performance
 */

/**
 * Throttle function - limits execution rate
 * Perfect for scroll events that need frequent updates (like progress bars)
 */
export function throttle<T extends (...args: any[]) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return function(this: any, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Debounce function - delays execution until after delay
 * Perfect for expensive operations that can wait (like resize handlers)
 */
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  return function(this: any, ...args: Parameters<T>) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
}

/**
 * Request Animation Frame throttle - best for visual updates
 * Syncs with browser's repaint cycle (60fps)
 */
export function rafThrottle<T extends (...args: any[]) => void>(
  func: T
): (...args: Parameters<T>) => void {
  let rafId: number | null = null;
  return function(this: any, ...args: Parameters<T>) {
    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        func.apply(this, args);
        rafId = null;
      });
    }
  };
}

/**
 * Optimized scroll position getter
 * Uses cached value within same frame to prevent layout thrashing
 */
let cachedScrollY: number | null = null;
let cacheFrame: number | null = null;

export function getScrollY(): number {
  const currentFrame = requestAnimationFrame(() => {});
  cancelAnimationFrame(currentFrame);
  
  if (cacheFrame === currentFrame && cachedScrollY !== null) {
    return cachedScrollY;
  }
  
  cachedScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
  cacheFrame = currentFrame;
  
  return cachedScrollY;
}

/**
 * Check if element is in viewport
 * Optimized version using IntersectionObserver when available
 */
export function isInViewport(element: HTMLElement, threshold = 0): boolean {
  if (!element) return false;
  
  const rect = element.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;
  
  return (
    rect.top >= -threshold &&
    rect.left >= -threshold &&
    rect.bottom <= windowHeight + threshold &&
    rect.right <= windowWidth + threshold
  );
}

/**
 * Passive event listener options
 * Improves scroll performance by telling browser the listener won't preventDefault
 */
let passiveSupported = false;
try {
  const options = Object.defineProperty({}, 'passive', {
    get: function() {
      passiveSupported = true;
      return false;
    }
  });
  const noop = () => {};
  window.addEventListener('testPassive' as any, noop, options);
  window.removeEventListener('testPassive' as any, noop, options);
} catch (err) {
  passiveSupported = false;
}

export { passiveSupported };

export const passiveOptions: AddEventListenerOptions | boolean = passiveSupported
  ? { passive: true }
  : false;
